// The words of activity history (g103): what an action, a change and the facts
// around them are called, and the shapes they are stored and sent in.
//
// Pure, like everything under shared/. These are names and types; the checks
// that hold a stored payload to them are the server's (`lib/activity-codec.ts`).
// This file defines the contract and writes nothing: the server's writer
// records an action (g104, g105), and an item's response carries its
// completion fields (g105) and nothing else of history.
//
// Every list below only grows. A value is never renamed or reused, because a
// row written under it is kept for the life of its owner's account. A change
// of shape takes a new payload version, and a reader keeps reading the old one.

/** The version of a stored action's `detail` and of a stored change's `payload`. */
export const ACTIVITY_PAYLOAD_VERSION = 1;
/** The version of the metadata a caller sends with a command. */
export const ACTION_META_VERSION = 1;
/** The version of the `groups` a push carries (g108). */
export const GROUP_META_VERSION = 1;
/** The version `activity_coverage` records the storage at. */
export const ACTIVITY_SCHEMA_VERSION = 1;

/** What a person or a connection asked for. One command is one action. */
export const ACTION_KINDS = [
  "list.created",
  "list.changed",
  "list.order_changed",
  "list.moved",
  "list.deleted",
  "list.imported",
  "item.created",
  "item.changed",
  "item.moved",
  "item.relisted",
  "item.deleted",
  "item.occurrence_completed",
  "item.occurrence_undone",
  "sharing.enabled",
  "sharing.disabled",
  "sharing.regenerated",
  "batch.items",
  // An undo of an action or a group, and a redo of an undo (g113). Nothing writes them until an inverse handler is enabled.
  "undo.applied",
  "undo.redone",
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export const ENTITY_TYPES = ["list", "item", "share_grant"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

/** What happened to one entity. `ended` is a share link's end, which is not a deletion. */
export const CHANGE_KINDS = ["created", "updated", "deleted", "ended"] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];

/**
 * What an update was, apart from which fields moved. One change can be
 * several: editing the text while ticking is one change with `fields` and
 * `completed`, never two that a later undo could each reverse.
 */
export const CHANGE_FACETS = [
  "fields",
  "completed",
  "reopened",
  "placement",
  "membership",
  "recurrence",
  "category_fill",
] as const;
export type ChangeFacet = (typeof CHANGE_FACETS)[number];

/** A refused command is not activity, so there is no third outcome here. */
export const OUTCOMES = ["applied", "unchanged"] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** A group's child can also have been refused. Unresolved is no outcome at all. */
export const CHILD_OUTCOMES = ["applied", "unchanged", "refused"] as const;
export type ChildOutcome = (typeof CHILD_OUTCOMES)[number];

export const GROUP_STATES = ["open", "closed", "incomplete"] as const;
export type GroupState = (typeof GROUP_STATES)[number];

/**
 * What made a group (g108). `clear_completed` is declared by the device that
 * confirmed it, with the children it expects, and closes as their outcomes
 * arrive. `mcp_batch` is declared and closed by the server, in the batch's own
 * transaction. An action whose change rows are already its exact set (a
 * list's delete, an import, a change of ordering with its fills) is one
 * action and has no group.
 */
export const GROUP_KINDS = ["clear_completed", "mcp_batch"] as const;
export type GroupKind = (typeof GROUP_KINDS)[number];

/** The kinds a device may declare through sync. */
export const DECLARED_GROUP_KINDS = ["clear_completed"] as const;
export type DeclaredGroupKind = (typeof DECLARED_GROUP_KINDS)[number];

/**
 * What closed a group: its last child's own outcome, the device saying which
 * children it will never send, or the server that made it whole.
 */
export const GROUP_CLOSERS = ["receipts", "device", "server"] as const;
export type GroupCloser = (typeof GROUP_CLOSERS)[number];

/** A list holds at most this many items, so no one confirmation names more. */
export const MAX_GROUP_CHILDREN = 2_000;
/** How many groups one push may speak of. */
export const MAX_PUSH_GROUPS = 4;
/** Why a child the device never sent has no action. */
export const NOT_SENT = "not_sent";

export const CHANNELS = ["sync", "shared_sync", "rest", "mcp", "system"] as const;
export type Channel = (typeof CHANNELS)[number];

/** Whose clock `occurredAt` is. `server` means the device sent no time. */
export const TIME_SOURCES = ["device", "server"] as const;
export type TimeSource = (typeof TIME_SOURCES)[number];

/** A device time is a claim. One that cannot be right is kept and marked, not corrected. */
export const TIME_FLAGS = ["implausible"] as const;
export type TimeFlag = (typeof TIME_FLAGS)[number];

/** Where an item's completion time came from. The same two clocks. */
export const COMPLETED_AT_SOURCES = TIME_SOURCES;
export type CompletedAtSource = TimeSource;

/**
 * How much an action can be trusted to be the whole story.
 *
 * - `full`: one command, sent whole under its own operation id.
 * - `legacy_no_operation_id`: the caller named no operation, so a retry
 *   cannot be told from the same command made twice.
 * - `legacy_coalesced`: an older app had already merged several taps into
 *   this one before it was sent. The taps are not recoverable.
 * - `pre_history_reversal`: it undoes something done before history began.
 */
export const COVERAGE = ["full", "legacy_no_operation_id", "legacy_coalesced", "pre_history_reversal"] as const;
export type Coverage = (typeof COVERAGE)[number];

export const COVERAGE_EVENTS = ["schema_installed", "capture_started", "capture_stopped", "gap"] as const;
export type CoverageEvent = (typeof COVERAGE_EVENTS)[number];

export const ACTOR_KINDS = ["session", "share_account", "share_guest", "oauth", "api_token", "system", "unknown"] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];

export const LABEL_SOURCES = ["token_name", "client_metadata", "configured"] as const;
export type LabelSource = (typeof LABEL_SOURCES)[number];

/**
 * Who is acting, as the server resolved it where the request was
 * authenticated. Nothing here is read from a body: there is no field for an
 * owner, a name or a robot's description of itself.
 *
 * The owner is not part of it. A visitor writing through a share link acts on
 * someone else's list, so the owner is passed beside the actor, never inside.
 */
export type ActorContext =
  /** The owner in the app. The session is noted, and is not who they are: a renewal changes it. */
  | { readonly kind: "session"; readonly identityId: string; readonly sessionId: string | null }
  /** A signed-in person on a share link. The grant is their authority, not their authorship. */
  | { readonly kind: "share_account"; readonly identityId: string; readonly grantId: string }
  | { readonly kind: "share_guest"; readonly identityId: string; readonly grantId: string }
  /** The grant is the connection. An access token changes hourly and is not. */
  | { readonly kind: "oauth"; readonly identityId: string; readonly grantId: string; readonly clientId: string }
  /** The token's row, not its name or its secret. */
  | { readonly kind: "api_token"; readonly identityId: string; readonly tokenId: string }
  | { readonly kind: "system"; readonly name: string }
  | { readonly kind: "unknown" };

/**
 * The stable name of a principal or connection, which is both the key of its
 * attribution record and the namespace its operation ids are unique in. Two
 * grants for one client are two scopes; a renewed session is the same one.
 */
export function actorKey(actor: ActorContext): string {
  switch (actor.kind) {
    case "session": return `account:${actor.identityId}`;
    case "share_account":
    case "share_guest": return `share:${actor.identityId}:${actor.grantId}`;
    case "oauth": return `oauth:${actor.grantId}`;
    case "api_token": return `token:${actor.tokenId}`;
    case "system": return `system:${actor.name}`;
    case "unknown": return "unknown";
  }
}

/**
 * Whether a receipt written under `stored` answers `actor` (g106). A
 * connection's receipt answers that connection and no other. A visitor's
 * answers the same visitor on any link to the list: a replaced link is new
 * authority for the same person, and the page resends what it had queued
 * through the new one. Whether the visitor may still write is the link's to
 * say, checked before any receipt is read.
 */
export function receiptAnswers(stored: string, actor: ActorContext): boolean {
  if (stored === actorKey(actor)) return true;
  return (actor.kind === "share_account" || actor.kind === "share_guest") && stored.startsWith(`share:${actor.identityId}:`);
}

/** When it happened, by the device, and how far to believe that. */
export interface ClockFacts {
  readonly occurredAt: number | null;
  /** IANA, and null with `occurredAt`. */
  readonly timeZone: string | null;
  readonly source: TimeSource;
  readonly flag: TimeFlag | null;
}

/** A reference a caller may send to something this command belongs to or answers. */
export interface ActionGroupRef {
  readonly id: string;
  readonly index: number;
}

/**
 * What a caller sends with a command: its own id for the command and when it
 * was made. Sent as `action` in a sync mutation or a tool's arguments, and as
 * the `Idempotency-Key` header's value for `operationId` on a REST call that
 * has no body. A command that names two different operation ids is refused.
 */
export interface ActionMetaInput {
  readonly v: typeof ACTION_META_VERSION;
  readonly operationId: string;
  readonly occurredAt?: number;
  readonly timeZone?: string;
  readonly group?: ActionGroupRef;
  /** The operation this one undoes, which may not have reached the server yet. */
  readonly reversesOperationId?: string;
  readonly causedByOperationId?: string;
}

/**
 * What a sync reply says while its server records history (g107): the version
 * of `action` it records. Absent from a server that does not record, and an
 * app queues whole actions only for a version it knows.
 */
export interface SyncRecording {
  readonly action: typeof ACTION_META_VERSION;
  /** The version of `groups` this server takes in a push (g108). Absent from one that takes none. */
  readonly group?: typeof GROUP_META_VERSION;
}

/** Whether a reply's server records the `action` this build sends. */
export function recordsActions(recording: unknown): boolean {
  return typeof recording === "object" && recording !== null && (recording as { action?: unknown }).action === ACTION_META_VERSION;
}

/** Whether a reply's server takes the groups this build declares (g108). Only a server that records does. */
export function recordsGroups(recording: unknown): boolean {
  return recordsActions(recording) && (recording as { group?: unknown }).group === GROUP_META_VERSION;
}

/**
 * A group as a push speaks of it (g108). `children` is the manifest: every
 * expected child's operation id, in the group's order, sent until a reply has
 * acknowledged the group and left out after. `notSent` names, by index, the
 * children the device will never send.
 */
export interface SyncGroupInput {
  readonly id: string;
  readonly kind: DeclaredGroupKind;
  readonly children?: readonly string[];
  readonly notSent?: readonly number[];
}

/**
 * What the reply says of each group a push spoke of. `unknown` is a group the
 * server holds no manifest for, which the device answers by sending it again.
 * `refused` is a manifest that differs from the one held, or another actor's.
 */
export interface SyncGroupReply {
  readonly id: string;
  readonly status: "open" | "closed" | "unknown" | "refused";
  readonly reason?: string;
}

/**
 * What a sync verdict says about the action its mutation became (g107), when
 * the mutation carried `action` and the server recorded it. `unchanged` is an
 * accepted command that changed nothing, and `replay` an answer to a command
 * already accepted. It names no action: the operation id is the caller's own.
 */
export interface SyncActionReceipt {
  readonly operationId: string;
  readonly outcome: Outcome;
  readonly recordedAt: number;
  readonly replay: boolean;
}

/** What a caller is told about the action its command became. Added to a result, never required of one. */
export interface ActionMetaOutput {
  readonly actionId: string;
  readonly operationId: string;
  readonly outcome: Outcome;
  readonly recordedAt: number;
}

/** The three fields every item response carries since g105. Null where no completion time is known. */
export interface ItemCompletionWire {
  readonly completedAt: number | null;
  readonly completedAtSource: CompletedAtSource | null;
  readonly completionEventId: string | null;
}

export interface DueFacts {
  readonly date: string;
  readonly time: string | null;
  readonly tz: string | null;
}

/** A repeating item's limited undo, as the row holds it. */
export interface CompletionToken {
  readonly id: string;
  readonly due: DueFacts;
  readonly lastDoneOn: string | null;
  /** The completion projection this completion replaced (g105). Absent on an older token. */
  readonly prior?: ItemCompletionWire;
}

/**
 * A whole list, for a create and a delete. Every key is present and null is a
 * value: a snapshot with a key missing is not one. No owner and no
 * `updatedAt`, which says when a row was last touched and nothing about it.
 */
export interface ListSnapshot {
  readonly id: string;
  readonly kind: "todo" | "shopping" | "other";
  readonly creatorId: string | null;
  readonly name: string;
  readonly orderKey: string;
  readonly orderBy: "category" | null;
  readonly createdAt: number;
  readonly deletedAt: number | null;
  readonly entityRevision: number;
  readonly itemOrderRevision: number;
}

/** A whole item, for a create and a delete. As `ListSnapshot`: every key, and null is a value. */
export interface ItemSnapshot extends ItemCompletionWire {
  readonly id: string;
  readonly listId: string;
  readonly creatorId: string | null;
  readonly text: string;
  readonly done: boolean;
  readonly quantity: string | null;
  readonly category: string | null;
  readonly note: string | null;
  readonly due: DueFacts | null;
  readonly repeat: "daily" | "weekly" | "monthly" | "yearly" | null;
  readonly lastDoneOn: string | null;
  readonly repeatAnchor: string | null;
  readonly occurrenceRevision: number;
  readonly completion: CompletionToken | null;
  readonly orderKey: string;
  readonly createdAt: number;
  readonly deletedAt: number | null;
  readonly entityRevision: number;
}

/** The facts of a share link's life. Never its token, its signature or its URL. */
export interface ShareGrantSnapshot {
  readonly id: string;
  readonly listId: string;
  readonly mode: "view" | "edit";
  readonly createdAt: number;
  readonly endedAt: number | null;
  readonly endReason: "disabled" | "regenerated" | "list_deleted" | "key_retired" | null;
}

/** Both sides are always present. Null is "it was empty", which is not "it did not change". */
export interface FieldChange<V> {
  readonly before: V;
  readonly after: V;
}

type Changes<S, K extends keyof S> = { readonly [P in K]?: FieldChange<S[P]> };

/** The fields of a list a change can name. A key left out did not change. */
export const LIST_CHANGE_FIELDS = ["name", "kind", "orderBy", "deletedAt"] as const;
export type ListFieldChanges = Changes<ListSnapshot, (typeof LIST_CHANGE_FIELDS)[number]>;

/**
 * The fields of an item a change can name. `orderKey` is not one of them: a
 * position is a `PositionFacts`, and a key is evidence inside it. Nor is
 * `entityRevision`, which the change row carries before and after.
 */
export const ITEM_CHANGE_FIELDS = [
  "listId", "text", "done", "quantity", "category", "note", "due", "repeat", "lastDoneOn", "repeatAnchor",
  "occurrenceRevision", "completion", "completedAt", "completedAtSource", "completionEventId", "deletedAt",
] as const;
export type ItemFieldChanges = Changes<ItemSnapshot, (typeof ITEM_CHANGE_FIELDS)[number]>;

export const SHARE_GRANT_CHANGE_FIELDS = ["endedAt", "endReason"] as const;
export type ShareGrantFieldChanges = Changes<ShareGrantSnapshot, (typeof SHARE_GRANT_CHANGE_FIELDS)[number]>;

/** Which sequence a position is in: one list's items, or its owner's lists. */
export type OrderScope =
  | { readonly kind: "list_items"; readonly listId: string }
  | { readonly kind: "owner_lists" };

/** Where a row sat, by its neighbours in stored order, ticked rows included. The key is evidence, never an instruction. */
export interface ResolvedPlacement {
  readonly scope: OrderScope;
  readonly afterId: string | null;
  readonly beforeId: string | null;
  readonly orderKey: string;
}

/**
 * A position, as asked for and as it came out. `requested` is what the caller
 * named and may name rows that had gone; `before` and `after` are what the
 * server found and made. The two are kept apart because an undo has to find
 * live neighbours again, and a replayed request is not one.
 */
export interface PositionFacts {
  /** Null where the caller named no neighbour, which is an append. */
  readonly requested: { readonly afterId: string | null; readonly beforeId: string | null } | null;
  /** Null for a row that was not there before: a create, or the list an item arrives in. */
  readonly before: ResolvedPlacement | null;
  /** Null for a row that is gone after. */
  readonly after: ResolvedPlacement | null;
  /** The revision of each sequence touched, keyed by `scopeKey`. A move between lists touches two. */
  readonly scopeRevisions: Readonly<Record<string, FieldChange<number>>>;
  /** How the list was shown, where that explains the move. */
  readonly view: "category" | null;
}

export function scopeKey(scope: OrderScope): string {
  return scope.kind === "owner_lists" ? "lists" : `list:${scope.listId}`;
}

/** Exactly which occurrence was completed or undone. A date alone is not enough: a schedule can be set back to one. */
export interface OccurrenceFacts {
  readonly due: DueFacts;
  readonly repeat: "daily" | "weekly" | "monthly" | "yearly";
  readonly repeatAnchor: string | null;
  readonly occurrenceRevision: number;
  readonly nextDue: DueFacts | null;
  /** The token the completion left, or the one an undo spent. */
  readonly completionId: string;
}

/** A blank category filled by the classifier, and by which version of it. */
export interface CategoryFillFacts {
  readonly classifierVersion: number;
}

interface ChangeBase {
  readonly v: typeof ACTIVITY_PAYLOAD_VERSION;
  readonly position?: PositionFacts;
}

/** What is stored in `activity_changes.payload`. */
export type ChangePayload =
  | (ChangeBase & { readonly entityType: "list"; readonly changeKind: "created" | "deleted"; readonly snapshot: ListSnapshot })
  | (ChangeBase & { readonly entityType: "list"; readonly changeKind: "updated"; readonly facets: readonly ChangeFacet[]; readonly fields: ListFieldChanges })
  | (ChangeBase & { readonly entityType: "item"; readonly changeKind: "created" | "deleted"; readonly snapshot: ItemSnapshot })
  | (ChangeBase & {
      readonly entityType: "item";
      readonly changeKind: "updated";
      readonly facets: readonly ChangeFacet[];
      readonly fields: ItemFieldChanges;
      readonly occurrence?: OccurrenceFacts;
      readonly categoryFill?: CategoryFillFacts;
    })
  | (ChangeBase & { readonly entityType: "share_grant"; readonly changeKind: "created"; readonly snapshot: ShareGrantSnapshot })
  | (ChangeBase & { readonly entityType: "share_grant"; readonly changeKind: "ended"; readonly snapshot: ShareGrantSnapshot; readonly fields: ShareGrantFieldChanges });

/** A payload written by a later build than this one. Kept whole and shown as unreadable, never guessed at. */
export interface UnknownChangePayload {
  readonly v: number;
  readonly unknown: true;
}

/**
 * A change whose content the sweep removed (g126), because the list or item it
 * describes was deleted 30 days before. What is left is what kind of change it
 * was and, for an update, which way it went: no name, text, field value,
 * snapshot or position. It is not a `ChangePayload`, whose version-1 shapes
 * are fixed, and it is never written by an ordinary write.
 */
export interface ExpiredChangePayload {
  readonly v: typeof ACTIVITY_PAYLOAD_VERSION;
  readonly expired: true;
  readonly entityType: EntityType;
  readonly changeKind: ChangeKind;
  readonly facets?: readonly ChangeFacet[];
}

/** One entity's change, as `activity_changes` holds it. */
export interface ChangeRecord {
  readonly id: string;
  readonly actionId: string;
  readonly ordinal: number;
  readonly entityType: EntityType;
  readonly entityId: string;
  readonly listId: string | null;
  /** The list an item left, so the move is found from both. */
  readonly otherListId: string | null;
  readonly changeKind: ChangeKind;
  readonly label: string | null;
  readonly revisionBefore: number | null;
  readonly revisionAfter: number | null;
  readonly changedFields: readonly string[];
  readonly payload: ChangePayload | UnknownChangePayload | ExpiredChangePayload;
}

/** What is stored in `activity_actions.detail`: a batch's indexed outcomes, or nothing. */
export interface ActionDetail {
  readonly v: typeof ACTIVITY_PAYLOAD_VERSION;
  /** For a batch that can partly succeed: what became of each entry, by its index. */
  readonly entries?: readonly { readonly index: number; readonly outcome: ChildOutcome; readonly code?: string }[];
}

/** One accepted command, as `activity_actions` holds it. */
export interface ActionEnvelope {
  readonly actionId: string;
  readonly sequence: number;
  readonly operationId: string;
  readonly operationIdSource: "client" | "server";
  readonly operationScope: string;
  readonly groupId: string | null;
  readonly groupIndex: number | null;
  readonly parentActionId: string | null;
  readonly causedByActionId: string | null;
  readonly reversesActionId: string | null;
  readonly reversesOperationId: string | null;
  readonly kind: ActionKind;
  readonly outcome: Outcome;
  readonly channel: Channel;
  /** The route or tool, named by the server. */
  readonly entryPoint: string;
  readonly clock: ClockFacts;
  readonly receivedAt: number;
  readonly recordedAt: number;
  readonly coverage: Coverage;
  readonly payloadVersion: number;
  readonly detail: ActionDetail | null;
}

/**
 * What a queued change waiting on a device is, to history. A record an older
 * app queued has no `actionVersion`: it may already hold several taps merged
 * into one, and nothing can take them apart again, so it is sent as one
 * action and says so. g107 gives a new record its version, id and time.
 */
export interface QueuedActionFacts {
  readonly actionVersion?: number;
  readonly operationId?: string;
  readonly op: string;
  /** Kept apart in the queue and never folded into: a recurrence edit, or a change of a list's ordering. */
  readonly ordered?: boolean;
}

/** The ops an older queue merged: a later tap on the same row was folded into the waiting one. */
const COALESCED_OPS: readonly string[] = ["upsert", "patch", "move", "delete"];

export function queuedCoverage(record: QueuedActionFacts): Coverage {
  if (record.actionVersion !== undefined) return "full";
  // An ordered record under its own id was one command when queued and still is.
  if (record.ordered && record.operationId) return "full";
  if (COALESCED_OPS.includes(record.op)) return "legacy_coalesced";
  return record.operationId ? "full" : "legacy_no_operation_id";
}

/* ------------------------------------------------------------ Reading history (g109) */

/** The version of a page of history and of an action's detail as a reader gets them. */
export const ACTIVITY_READ_VERSION = 1;
/** Actions in a page when the reader names no limit, and the most one may name. */
export const ACTIVITY_PAGE_DEFAULT = 50;
export const ACTIVITY_PAGE_MAX = 100;
/** A page stops before it would pass this many bytes of JSON, and always holds one action. */
export const ACTIVITY_PAGE_BYTES = 256 * 1024;
/** An action's detail stops its changes before this many bytes, and always holds one change. */
export const ACTIVITY_DETAIL_BYTES = 512 * 1024;
/** A label in a summary is cut to this many characters. The detail carries it whole. */
export const ACTIVITY_LABEL_CHARS = 200;
/** How many of an action's changes its summary names. The rest are in its detail. */
export const ACTIVITY_SUMMARY_CHANGES = 3;

/** What a reader filters by kind with: the part of a kind before its dot. */
export const ACTION_FAMILIES = ["list", "item", "sharing", "batch", "undo"] as const;
export type ActionFamily = (typeof ACTION_FAMILIES)[number];

/**
 * The connection an action came through, where it came through one. `id` is
 * the token's, the OAuth grant's or the share link's row, which is no secret:
 * a link works only with its signature. `claimed` is a name the connection
 * gave itself, which nobody checked.
 */
export interface ConnectionView {
  readonly kind: "api_token" | "oauth" | "share_link";
  readonly id: string;
  readonly label: string | null;
  readonly labelSource: LabelSource | null;
  readonly claimed: boolean;
}

/**
 * Who acted, as a reader is told. `id` is the attribution record's, never an
 * account's or an identity's. `name` is a person's display name, or a guest's
 * own word for themselves, and never an email. A retired actor has no name and
 * no connection label, and is never named again.
 */
export interface ActorView {
  readonly id: string;
  readonly kind: ActorKind;
  /** The reader's own account, through any of its sessions or connections. */
  readonly you: boolean;
  readonly name: string | null;
  readonly retired: boolean;
  readonly connection: ConnectionView | null;
}

/**
 * A group as a reader is told. `finished` is true only once it closed: an
 * open group is unfinished, never failed, and `unresolved` says how many of
 * its children nobody has heard of yet.
 */
export interface GroupView {
  readonly id: string;
  readonly kind: string;
  readonly state: string;
  readonly finished: boolean;
  readonly expected: number | null;
  readonly applied: number;
  readonly unchanged: number;
  readonly refused: number;
  readonly unresolved: number;
  readonly closedBy: string | null;
  readonly declaredAt: number;
  readonly closedAt: number | null;
  /** Why children were refused, by code, at most ten codes. */
  readonly refusals: readonly { readonly code: string; readonly count: number }[];
}

/**
 * One change as a summary names it: what it touched and what it was called,
 * with no payload. `deleted` is the entity as it stands now.
 */
export interface ChangeLine {
  readonly ordinal: number;
  readonly entityType: string;
  readonly entityId: string;
  readonly listId: string | null;
  readonly otherListId: string | null;
  readonly changeKind: string;
  readonly label: string | null;
  readonly labelTruncated: boolean;
  readonly deleted: boolean;
  readonly changedFields: readonly string[];
  /** What an update was, apart from which fields moved: `completed` or `reopened` says which way a tick went (g110). Empty for anything else or a payload this build cannot read. */
  readonly facets: readonly string[];
  /**
   * The content is no longer kept (g126): the list or item was deleted 30 days
   * before, so the label is null and a detail's payload is null too, though
   * the change is not unreadable. Absent from a page an older server wrote.
   */
  readonly expired?: boolean;
}

/** One change whole, as an action's detail gives it. A payload this build cannot read is null and `unreadable`; one whose content expired is null and not. */
export interface ChangeView extends ChangeLine {
  readonly revisionBefore: number | null;
  readonly revisionAfter: number | null;
  readonly payloadVersion: number;
  readonly unreadable: boolean;
  readonly payload: ChangePayload | null;
}

/**
 * One action in a page. `known` is false for a kind a later build wrote,
 * which is shown as it is and never guessed at. Both server times and the
 * device's claim are separate, and `operationId` is the caller's own id, so
 * a device can match its own queue.
 */
export interface ActionSummary {
  readonly id: string;
  readonly kind: string;
  readonly known: boolean;
  readonly outcome: string;
  readonly coverage: string;
  readonly channel: string;
  readonly entryPoint: string;
  readonly operationId: string;
  readonly recordedAt: number;
  readonly receivedAt: number;
  readonly occurredAt: number | null;
  readonly timeZone: string | null;
  readonly timeSource: string;
  readonly timeFlag: string | null;
  readonly parentActionId: string | null;
  readonly causedByActionId: string | null;
  readonly reversesActionId: string | null;
  readonly actor: ActorView;
  readonly changeCount: number;
  readonly changes: readonly ChangeLine[];
  /** Null where it belongs to no group. `index` is its place in the group, where it has one. */
  readonly group: (GroupView & { readonly index: number | null }) | null;
}

/** Where a history begins and where it has gaps: the server's own rows and this owner's. */
export interface CoverageView {
  readonly kind: string;
  readonly at: number;
}

/**
 * A page of history, newest first. `next` is opaque and continues this page
 * with the same filters; only a null `next` is the end. Actions recorded after
 * the first page never shift a later one. The first page also says where
 * history begins.
 */
export interface ActivityPage {
  readonly v: typeof ACTIVITY_READ_VERSION;
  readonly actions: readonly ActionSummary[];
  readonly next: string | null;
  readonly coverage?: readonly CoverageView[];
}

/** One action with a page of its changes. `entries` is a batch's outcome for each entry. */
export interface ActionDetailView {
  readonly v: typeof ACTIVITY_READ_VERSION;
  readonly action: ActionSummary;
  readonly entries: ActionDetail["entries"] | null;
  readonly changes: {
    readonly total: number;
    readonly items: readonly ChangeView[];
    readonly next: string | null;
  };
}

/**
 * What an owner's sync reply says of their history while it can be read
 * (g109), so a device knows when what it keeps of it is stale. `head` is the
 * newest action, and `epoch` moves when a name, a label or coverage changes:
 * a cached page's names are then out of date though no action is new.
 */
export interface ActivityStamp {
  readonly v: typeof ACTIVITY_READ_VERSION;
  readonly head: string | null;
  readonly epoch: string;
}
